import { useEffect, useState } from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

/**
 * Fetches additional profile data (bio, headline) and hashtags for the sidebar.
 *
 * The conversation JSONB includes basic profile fields but not bio or headline.
 * This hook fetches them from the user_profiles PostgREST endpoint, and also
 * fetches the profile's hashtags for the "Expert on:" / "Covers:" section.
 *
 * @param {string} profileSlug - The profile's URL slug
 * @param {number|string} profileId - The profile's ID (used for hashtag lookup)
 * @returns {{ bio: string, headline: string, hashtags: Array, teamId: number|null }}
 */
function useProfileData(profileSlug, profileId) {
  const [bio, setBio] = useState('');
  const [headline, setHeadline] = useState('');
  const [hashtags, setHashtags] = useState([]);
  const [teamId, setTeamId] = useState(null);
  useEffect(() => {
    setBio('');
    setHeadline('');
    setHashtags([]);
    setTeamId(null);

    if (!profileSlug || !profileId) {
      return undefined;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        const [profileResult, hashtagsResult] = await Promise.all([
          qapi.userProfiles.get(profileSlug),
          qapi.hashtags.getAllForProfileIdForDisplay(profileId, { page: 1, per_page: 50 }),
        ]);

        if (cancelled) return;

        if (!profileResult.error && profileResult.data) {
          const profile = Array.isArray(profileResult.data) ? profileResult.data[0] : profileResult.data;
          if (profile) {
            setBio(profile.bio || '');
            setHeadline(profile.headline || '');
            setTeamId(profile.team_id || null);
          }
        } else if (profileResult.error) {
          qlog.error('Failed to fetch profile details:', profileResult.error);
        }

        if (!hashtagsResult.error && hashtagsResult.data) {
          setHashtags(Array.isArray(hashtagsResult.data) ? hashtagsResult.data : []);
        } else if (hashtagsResult.error) {
          qlog.error('Failed to fetch hashtags:', hashtagsResult.error);
        }
      } catch (error) {
        if (!cancelled) {
          qlog.error('Failed to fetch sidebar profile data:', error);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [profileSlug, profileId]);

  return {
 bio, headline, hashtags, teamId,
};
}

export default useProfileData;
